import React from 'react';
import { motion } from 'framer-motion';
import { AlertOctagon, Clock, Shield, Zap, RefreshCw, FileSearch, CheckCircle, ArrowRight, Brain, Target, Lock, Laptop } from 'lucide-react';

interface IncidentResponseProps {
  setCurrentPage: (page: string) => void;
}

const IncidentResponse: React.FC<IncidentResponseProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Clock,
      title: "24/7 Response Team",
      description: "Round-the-clock incident response team ready to handle security incidents at any time."
    },
    {
      icon: Zap,
      title: "Rapid Response",
      description: "Swift incident detection and response to minimize impact and reduce downtime."
    },
    {
      icon: Shield,
      title: "Containment Strategy",
      description: "Effective containment procedures to prevent incident escalation and limit damage."
    },
    {
      icon: RefreshCw,
      title: "Recovery Planning",
      description: "Comprehensive recovery plans to restore operations and strengthen security."
    }
  ];

  const services = [
    {
      title: "Incident Detection",
      items: [
        "Real-time monitoring",
        "Automated alert systems",
        "Threat pattern recognition",
        "Behavioral analysis"
      ]
    },
    {
      title: "Response Protocol",
      items: [
        "Immediate threat containment",
        "Impact assessment",
        "Stakeholder communication",
        "Evidence preservation"
      ]
    },
    {
      title: "Recovery Process",
      items: [
        "System restoration",
        "Data recovery",
        "Security enhancement",
        "Post-incident analysis"
      ]
    }
  ];

  const solutions = [
    {
      icon: Brain,
      title: "AI-Powered Response",
      description: "Advanced artificial intelligence systems for automated incident detection and response."
    },
    {
      icon: Target,
      title: "Targeted Recovery",
      description: "Customized recovery strategies based on incident type and business impact."
    },
    {
      icon: Lock,
      title: "Security Hardening",
      description: "Post-incident security improvements to prevent future occurrences."
    }
  ];

  const steps = [
    {
      icon: AlertOctagon,
      title: "1. Detection & Analysis",
      description: "Rapid identification and analysis of security incidents using advanced detection systems."
    },
    {
      icon: Shield,
      title: "2. Containment",
      description: "Swift implementation of containment measures to prevent incident spread."
    },
    {
      icon: FileSearch,
      title: "3. Investigation",
      description: "Thorough investigation to understand the incident scope and impact."
    },
    {
      icon: Laptop,
      title: "4. Recovery",
      description: "Systematic recovery procedures to restore normal operations securely."
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <AlertOctagon className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">
              Incident Response & Recovery
            </span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
              Swift and effective incident response solutions to protect your business and ensure rapid recovery
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Response Capabilities</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive incident response and recovery features
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mb-6">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Response Steps */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Incident Response Process</span>
            </h2>
            <p className="text-lg text-muted">
              Our systematic approach to handling security incidents
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl mb-6">
                  <step.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4">
                  {step.title}
                </h3>
                <p className="text-muted">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Response Services</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive incident response and recovery services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <h3 className="text-2xl font-semibold mb-6">
                  {service.title}
                </h3>
                <ul className="space-y-4">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Advanced Solutions */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Advanced Solutions</span>
            </h2>
            <p className="text-lg text-muted">
              Cutting-edge incident response and recovery solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 text-center hover-card"
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <solution.icon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold mb-4">
                  {solution.title}
                </h3>
                <p className="text-muted">
                  {solution.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Ready to Strengthen Your Incident Response?
              </h2>
              <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
                Get started with our incident response and recovery solutions
              </p>
              <button 
                onClick={() => setCurrentPage('get-started')}
                className="btn-primary"
              >
                <span className="flex items-center justify-center">
                  Request Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default IncidentResponse;